import Sidebar from "@/components/sidebar/Sidebar"
import { Section } from "@/model/CustomerSupport"
import { ROUTES } from "@/routes"
import cn from "@/utils/cn"
import { ReactNode, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { SectionContext } from "./SectionContext"

type SidebarLayoutProps = {
  children: ReactNode
}

const sectionRoots = {
  population: ROUTES.POPULATION.ROOT,
  support: ROUTES.SUPPORT.ROOT,
} as const

const SidebarLayout = ({ children }: SidebarLayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const lastVisitedPaths = useRef<Record<Section, string>>({ ...sectionRoots })

  const activeSection = location.pathname.startsWith(ROUTES.SUPPORT.ROOT)
    ? "support"
    : "population"

  const currentSectionRoot = sectionRoots[activeSection]

  useEffect(() => {
    lastVisitedPaths.current[activeSection] =
      location.pathname + location.search
  }, [location.pathname, location.search, activeSection])

  const goBackToSectionRoot = () => navigate(currentSectionRoot)

  const toggleSidebar = () => {
    setIsExpanded((prev) => !prev)
  }

  const handleNavigateToSection = (section: Section) => {
    navigate(lastVisitedPaths.current[section])
  }

  return (
    <SectionContext.Provider
      value={{
        currentSection: activeSection,
        currentSectionRootPath: currentSectionRoot,
        goBackToSectionRoot,
      }}
    >
      <div className="flex h-screen overflow-hidden">
        <Sidebar isExpanded={isExpanded} onToggle={toggleSidebar}>
          <Sidebar.Item
            icon="groups"
            label="Patient Population"
            active={activeSection === "population"}
            onClick={() => handleNavigateToSection("population")}
            isExpanded={isExpanded}
          />
          <Sidebar.Item
            icon="support_agent"
            label="Customer Support"
            active={activeSection === "support"}
            onClick={() => handleNavigateToSection("support")}
            isExpanded={isExpanded}
          />
        </Sidebar>

        <main
          className={cn(
            "grow overflow-auto transition-all duration-300",
            isExpanded ? "ml-60" : "ml-16"
          )}
        >
          {children}
        </main>
      </div>
    </SectionContext.Provider>
  )
}

export default SidebarLayout
